import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button, Spin, Typography, Modal, Tooltip, Space } from 'antd';
import { SaveOutlined, SettingOutlined, WarningOutlined } from '@ant-design/icons';
import { HotTable } from '@handsontable/react';
import { registerAllModules } from 'handsontable/registry';
import Handsontable from 'handsontable';
import dayjs from 'dayjs';
import 'handsontable/dist/handsontable.full.css';
import './styles.css';

// Tüm modülleri kaydet
registerAllModules();

const { Text } = Typography;

// Sütun genişliğini başlık uzunluğuna göre hesapla
const calculateColumnWidth = (title) => {
  if (!title) return 150;
  
  // Minimum genişlik
  const minWidth = 150;
  
  // Her karakter için yaklaşık genişlik (px)
  const charWidth = 10;
  
  // Bir satırda maksimum karakter sayısı
  const maxCharsPerLine = 15;
  
  // Başlığı kelimelere böl
  const words = title.split(' ');
  
  // Toplam satır sayısını tahmin et
  const lines = Math.ceil(title.length / maxCharsPerLine);
  
  // En uzun kelimeyi bul
  const longestWord = words.reduce((max, word) => 
    word.length > max.length ? word : max, '');
    
  // En uzun kelimenin genişliği
  const longestWordWidth = longestWord.length * charWidth;
  
  // Başlık uzunluğuna göre hesaplanan genişlik
  const calculatedWidth = Math.max(
    longestWordWidth,
    title.length * (charWidth / lines)
  );
  
  // Hesaplanan genişliğe göre kategori belirle
  if (title.length > 30) return 250; // Çok uzun başlıklar
  if (title.length > 20) return 200; // Uzun başlıklar
  if (title.length > 10) return 170; // Orta uzunlukta başlıklar
  
  return Math.max(minWidth, calculatedWidth);
};

const YearlyMetricsTable = ({ data, monthlyMetrics, selectedYear, loading, onSave }) => {
  const [changedData, setChangedData] = useState({});
  const hotTableRef = useRef(null);
  
  // Tabloda kullanılacak sütunlar
  const columns = useMemo(() => {
    if (!data || data.length === 0 || !data[0]) return [];
    
    // İlk sütun ay adı
    const cols = [
      {
        data: 'monthName',
        type: 'text',
        title: 'Ay',
        readOnly: true,
        width: 150
      }
    ];
    
    // Tablonun ilk satırından metrik bilgilerini al
    const firstRow = data[0];
    const metricKeys = Object.keys(firstRow)
      .filter(key => key.startsWith('metric_') && !key.endsWith('_info'));
    
    // Her metrik için bir sütun ekle
    metricKeys.forEach(metricKey => {
      const metricInfoKey = `${metricKey}_info`;
      const metricInfo = firstRow[metricInfoKey] || {};
      
      cols.push({
        data: metricKey,
        type: 'numeric',
        title: metricInfo.name || 'Metrik',
        numericFormat: {
          pattern: '0,0.00',
          culture: 'tr-TR'
        },
        // Özel renderer ile otomatik hesaplanan metrikler özel gösterilecek
        renderer: function(instance, td, row, col, prop, value, cellProperties) {
          Handsontable.renderers.NumericRenderer.apply(this, arguments);
          
          // Metrik bilgilerini al
          const metricInfoKey = `${prop}_info`;
          const rowData = instance.getSourceDataAtRow(0); // İlk satırda metrik bilgileri var
          const metricInfo = rowData[metricInfoKey];
          
          // Otomatik hesaplanan metrik mi kontrol et
          if (metricInfo && metricInfo.isAutoCalculated) {
            td.style.backgroundColor = '#f5f5f5';
            td.title = 'Otomatik hesaplanır. Değiştirmek istediğinize emin misiniz?';
          }
          
          // Veri tipine göre özel formatlamalar
          if (metricInfo && value !== null && value !== undefined) {
            const dataType = metricInfo.dataType;
            if (dataType === 'percentage') {
              // Yüzde işareti ekle
              const numValue = parseFloat(value);
              if (!isNaN(numValue)) {
                td.innerHTML = `${numValue.toLocaleString('tr-TR')}%`;
              }
            } else if (dataType === 'currency') {
              // Kuruşları kaldır (tam sayıya yuvarla)
              const numValue = parseFloat(value);
              if (!isNaN(numValue)) {
                td.innerHTML = `${Math.round(numValue).toLocaleString('tr-TR')} ₺`;
              }
            }
          }
          
          return td;
        },
        // Sütun genişliği - başlık uzunluğuna göre ayarla
        width: calculateColumnWidth(metricInfo.name || 'Metrik')
      });
    });
    
    return cols;
  }, [data]);
  
  // Bir değer değiştirildiğinde
  const handleAfterChange = (changes, source) => {
    if (!changes || source === 'loadData') return;
    
    // Değişiklikleri topla
    const newChangedData = { ...changedData };
    
    changes.forEach(([row, prop, oldValue, newValue]) => {
      // Ay adı değiştirilmeye çalışılıyorsa engelle
      if (prop === 'monthName') return;
      
      // Metrik bilgilerini al
      if (!prop.startsWith('metric_')) return;
      
      // Ay bilgisini al
      const rowData = hotTableRef.current.hotInstance.getSourceDataAtRow(row);
      if (!rowData || !rowData.monthCode) return;
      
      const monthDate = rowData.monthCode;
      
      // Değer aynıysa değişikliği kaydetme
      if (oldValue === newValue) {
        delete newChangedData[`${prop}_${monthDate}`];
        return;
      }
      
      // Metrik ID'sini prop adından çıkar (metric_123 => 123)
      const metricId = prop.replace('metric_', '');
      
      // Metrik bilgilerini al
      const metricInfoKey = `${prop}_info`;
      const firstRowData = hotTableRef.current.hotInstance.getSourceDataAtRow(0);
      const metricInfo = firstRowData[metricInfoKey] || {};
      
      // Değişikliği kaydet - key formatı: "metricId_YYYY-MM"
      newChangedData[`${metricId}_${monthDate}`] = {
        oldValue,
        newValue,
        isAutoCalculated: metricInfo.isAutoCalculated
      };
    });
    
    setChangedData(newChangedData);
  };
  
  // Değişiklikleri kaydetmeden önce otomatik hesaplanan metriklerde onay al
  const handleSave = () => {
    // Otomatik hesaplanan metrikler değiştirilmiş mi kontrol et
    const hasAutoCalculatedChanges = Object.entries(changedData).some(
      ([key, value]) => value.isAutoCalculated
    );
    
    if (hasAutoCalculatedChanges) {
      // Onay modalı göster
      Modal.confirm({
        title: 'Otomatik Hesaplanan Değerleri Değiştirme',
        icon: <WarningOutlined />,
        content: 'Otomatik hesaplanan bazı değerleri değiştirmek üzeresiniz. Bu değerler manuel olarak işaretlenecek ve artık otomatik hesaplamalardan etkilenmeyecektir. Devam etmek istiyor musunuz?',
        okText: 'Evet, Manuel Değiştir',
        cancelText: 'İptal',
        onOk() {
          // Tüm değişiklikleri kaydet
          onSave(changedData);
          // Değişiklik listesini temizle
          setChangedData({});
        }
      });
    } else {
      // Direkt kaydet
      onSave(changedData);
      setChangedData({});
    }
  };
  
  // Herhangi bir değişiklik var mı kontrol et
  const hasChanges = Object.keys(changedData).length > 0;
  
  // Otomatik hesaplanan metrikler ve tarih kısıtlamaları için hücre özellikleri
  const cellProperties = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    const cells = [];
    const firstRow = data[0];
    const currentDate = dayjs();
    const currentMonth = currentDate.format('YYYY-MM');
    
    // Tüm metrik sütunlarını bul
    const metricKeys = Object.keys(firstRow)
      .filter(key => key.startsWith('metric_') && !key.endsWith('_info'));
    
    // Her metrik için her satır için hücre özelliklerini tanımla
    metricKeys.forEach((metricKey, colIndex) => {
      const metricInfoKey = `${metricKey}_info`;
      const metricInfo = firstRow[metricInfoKey];
      
      // Tüm satırlar için
      data.forEach((rowData, rowIndex) => {
        const rowMonth = rowData.monthCode; // YYYY-MM formatında
        const isCurrentOrFutureMonth = rowMonth >= currentMonth;
        
        let cellProperty = {
          row: rowIndex,
          col: colIndex + 1, // +1 çünkü ilk sütun ay adı
          className: '',
          readOnly: false,
          comment: null
        };
        
        // Bu ay ve gelecek aylara veri girişi yasak
        if (isCurrentOrFutureMonth) {
          cellProperty.readOnly = true;
          cellProperty.className = 'future-month-cell';
          cellProperty.comment = { value: 'Bu ay ve gelecek aylara veri girişi yapılamaz.' };
        }
        // Otomatik hesaplanan metrikler
        else if (metricInfo && metricInfo.isAutoCalculated) {
          cellProperty.className = 'auto-calculated-cell';
          cellProperty.comment = { value: 'Bu değer otomatik hesaplanır. Değiştirmek istediğinize emin misiniz?' };
        }
        
        cells.push(cellProperty);
      });
    });
    
    return cells;
  }, [data]);
  
  // Veri yoksa uyarı göster
  if (!data || data.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        {loading ? (
          <Spin size="large" />
        ) : (
          <Text type="secondary">Bu marka için yıllık metrik verisi bulunamadı.</Text>
        )}
      </div>
    );
  }
  
  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Text strong>
          Toplam {columns.length - 1} metrik, {data.length} ay ({selectedYear})
        </Text>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSave}
          disabled={!hasChanges}
          loading={loading}
        >
          Değişiklikleri Kaydet ({Object.keys(changedData).length})
        </Button>
      </div>
      
      <div className="yearly-metrics-hot-wrapper">
        <HotTable
          ref={hotTableRef}
          data={data}
          colHeaders={true}
          rowHeaders={true}
          width="100%"
          height="auto"
          licenseKey="non-commercial-and-evaluation"
          columns={columns}
          afterChange={handleAfterChange}
          stretchH="none"
          className="yearly-metrics-table"
          fixedRowsTop={0}
          fixedColumnsLeft={1}
          manualColumnResize={true}
          contextMenu={true}
          comments={true}
          cell={cellProperties}
          columnHeaderHeight={90}
          wordWrap={true}
          beforeChange={(changes, source) => {
            // Değişiklik yapılıyor mu kontrol et
            if (source === 'edit') {
              // Her bir değişiklik için
              return changes.map(change => {
                const [row, prop, oldValue, newValue] = change;
                
                // Ay adı değiştirilmeye çalışılıyorsa engelle
                if (prop === 'monthName') {
                  return false;
                }
                
                // Değişime izin ver
                return change;
              });
            }
            return changes;
          }}
          afterGetColHeader={(col, TH) => {
            // Metrik başlıkları için özel stil
            if (col >= 1) { // İlk sütun değilse (ay değil)
              Handsontable.dom.addClass(TH, 'metric-header');
              
              // Metrik bilgilerini al 
              if (data[0]) {
                const columnProp = columns[col].data;
                const metricInfoKey = `${columnProp}_info`;
                const metricInfo = data[0][metricInfoKey];
                
                // Başlık metnini al ve formatla
                const headerText = metricInfo ? (metricInfo.name || 'Metrik') : 'Metrik';
                const spanElement = TH.querySelector('.colHeader');
                
                if (spanElement) {
                  // Uzun başlıkları satırlara böl
                  const words = headerText.split(' ');
                  let formattedHeader = '';
                  
                  // Her 2-3 kelimeyi bir satıra yerleştir
                  for (let i = 0; i < words.length; i++) {
                    formattedHeader += words[i];
                    if ((i + 1) % 2 === 0 && i < words.length - 1) {
                      formattedHeader += '<br>';
                    } else if (i < words.length - 1) {
                      formattedHeader += ' ';
                    }
                  }
                  
                  spanElement.innerHTML = formattedHeader;
                  // Sabit yükseklik ayarla
                  TH.style.height = '90px';
                  TH.style.whiteSpace = 'normal';
                  TH.style.verticalAlign = 'middle';
                  TH.style.textAlign = 'center';
                  TH.style.padding = '5px';
                  TH.style.overflow = 'hidden';
                }
                
                // Otomatik hesaplanan başlıklara özel stil ekle
                if (metricInfo && metricInfo.isAutoCalculated) {
                  Handsontable.dom.addClass(TH, 'auto-calculated-header');
                  TH.title = 'Otomatik Hesaplanan Metrik';
                }
              }
            }
          }}
        />
      </div>
      
      <div style={{ marginTop: 16 }}>
        <Space>
          <div style={{ display: 'flex', alignItems: 'center', marginRight: 16 }}>
            <div style={{ width: 20, height: 20, backgroundColor: '#f5f5f5', marginRight: 8, border: '1px solid #d9d9d9' }}></div>
            <Text type="secondary">Otomatik Hesaplanan</Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ width: 20, height: 20, backgroundColor: '#fff', marginRight: 8, border: '1px solid #d9d9d9' }}></div>
            <Text type="secondary">Manuel Girilecek</Text>
          </div>
        </Space>
      </div>
    </div>
  );
};

export default YearlyMetricsTable; 